import React, { useState, useRef, useEffect } from 'react';
import { Save } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';

interface ProfileInfoProps {
  user: {
    id: number;
    full_name: string;
    email: string;
    phone?: string;
    nik?: string;
  };
  onUpdate: (data: any) => void;
  loading: boolean;
}

export default function ProfileInfo({ user, onUpdate, loading }: ProfileInfoProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    full_name: user.full_name || '',
    email: user.email || '',
    phone: user.phone || '',
    nik: user.nik || '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Update formData when user prop changes (after successful save)
  useEffect(() => {
    setFormData({
      full_name: user.full_name || '',
      email: user.email || '',
      phone: user.phone || '',
      nik: user.nik || '',
    });
  }, [user]);

  const handleChange = (field: string, value: string) => {
    // For NIK field, only allow numeric input
    if (field === 'nik') {
      value = value.replace(/\D/g, ''); // Remove non-numeric characters
      // Limit to 16 digits
      if (value.length > 16) {
        value = value.slice(0, 16);
      }
    }

    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleInvalid = (e: React.InvalidEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.full_name || formData.full_name.trim() === '') {
      newErrors.full_name = 'Nama lengkap harus diisi';
    }

    if (!formData.email || formData.email.trim() === '') {
      newErrors.email = 'Email harus diisi';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Format email tidak valid';
    }

    if (formData.nik && formData.nik.trim() !== '') {
      if (!/^\d{16}$/.test(formData.nik)) {
        newErrors.nik = 'NIK harus 16 digit angka';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleButtonClick = () => {
    if (validateForm()) {
      // Prepare data to send - only include fields that have values or are required
      const dataToSend = {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone || null,
        nik: formData.nik || null,
      };

      onUpdate(dataToSend);
    }
  };

  return (
    <div className="relative">
      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center rounded-lg">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full"></div>
            <p className="text-blue-600 font-medium text-lg">Menyimpan perubahan...</p>
            <p className="text-gray-500 text-sm">Mohon tunggu sebentar</p>
          </div>
        </div>
      )}

      <form ref={formRef} onSubmit={handleSubmit} noValidate className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Full Name */}
          <div className="space-y-2">
            <Label htmlFor="full_name">Nama Lengkap *</Label>
            <Input
              id="full_name"
              value={formData.full_name}
              onChange={e => handleChange('full_name', e.target.value)}
              onInvalid={handleInvalid}
              className={`focus:outline-none focus:border-blue-500 transition-colors duration-200 ${
                errors.full_name ? 'border-red-500' : ''
              }`}
            />
            {errors.full_name && <p className="text-sm text-red-600">{errors.full_name}</p>}
          </div>

          {/* NIK */}
          <div className="space-y-2">
            <Label htmlFor="nik">NIK</Label>
            <Input
              id="nik"
              type="text"
              value={formData.nik}
              onChange={e => handleChange('nik', e.target.value)}
              placeholder="16 digit nomor"
              maxLength={16}
              onInvalid={handleInvalid}
              className={`focus:outline-none focus:border-blue-500 transition-colors duration-200 ${
                errors.nik ? 'border-red-500' : ''
              }`}
            />
            {errors.nik && <p className="text-sm text-red-600">{errors.nik}</p>}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="text"
              value={formData.email}
              onChange={e => handleChange('email', e.target.value)}
              onInvalid={handleInvalid}
              className={`focus:outline-none focus:border-blue-500 transition-colors duration-200 ${
                errors.email ? 'border-red-500' : ''
              }`}
            />
            {errors.email && <p className="text-sm text-red-600">{errors.email}</p>}
          </div>

          {/* Phone */}
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="phone">Nomor Telepon</Label>
            <Input
              id="phone"
              type="text"
              value={formData.phone}
              onChange={e => handleChange('phone', e.target.value)}
              placeholder="+62 812-3456-7890"
              onInvalid={handleInvalid}
              className="focus:outline-none focus:border-blue-500 transition-colors duration-200"
            />
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button
            type="button"
            onClick={handleButtonClick}
            disabled={loading}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Save className="w-4 h-4" />
            {loading ? 'Menyimpan...' : 'Simpan Perubahan'}
          </Button>
        </div>
      </form>
    </div>
  );
}
